import {
  useCallback, useMemo, useState, ReactNode,
} from 'react';
import {
  Fade as MUIFade,
  FadeProps as MUIFadeProps,
  Popper as MUIPopper,
  PopperProps as MUIPopperProps,
  SxProps,
} from '@mui/material';
import { merge } from 'lodash';
import { boxShadow, P4, S1 } from '../../theme';
import PopperArrow from './components/PopperArrow';
import Box from '../Box';
import { mergeSX } from '../../services/helpers';

export { default as PopperTitle } from './components/PopperTitle';
export { default as PopperContent } from './components/PopperContent';
export { default as PopperActions } from './components/PopperActions';

type TPopperPadding = Partial<{
  top: number;
  right: number;
  bottom: number;
  left: number;
}>

interface IBuildSXParams {
  arrowBackground?: string;
}

export type TPopperProps = MUIPopperProps & IBuildSXParams & {
  offsetX?: number;
  offsetY?: number;
  flipPadding?: TPopperPadding;
  arrow?: boolean;
  preventOverflowPadding?: TPopperPadding;
  fadeTimeout?: number;
  onFadeExited?: MUIFadeProps['onExited'];
  containerSX?: SxProps;
  children: ReactNode;
}

const buildPopperSX = ({ arrowBackground }: IBuildSXParams) => ({
  '& .popper-arrow': {
    width: 0,
    height: 0,
  },
  '&[data-popper-placement*="bottom"] .popper-arrow': {
    top: '-14px',
    borderBottom: `20px solid ${arrowBackground}`,
    borderLeft: '13px solid transparent',
    borderRight: '13px solid transparent',
  },
});

const buildContainerSX = () => ({
  boxShadow,
  background: S1,
  borderRadius: '20px',
  overflow: 'hidden',
});

const DEFAULT_PADDING = {
  top: 16,
  right: 16,
  bottom: 16,
  left: 16,
};

const Popper = (props: TPopperProps) => {
  const {
    children,
    offsetX = 0,
    offsetY = 10,
    flipPadding,
    preventOverflowPadding,
    fadeTimeout = 150,
    onFadeExited,
    arrowBackground = P4,
    arrow = false,
    containerSX,
    sx,
    ...restProps
  } = props;

  const [arrowRef, setArrowRef] = useState<HTMLDivElement | null>(null);

  const popperModifiers = useMemo<TPopperProps['modifiers']>(() => [
    {
      name: 'offset',
      options: { offset: [offsetX, offsetY] },
    },
    {
      name: 'flip',
      options: { padding: merge({}, DEFAULT_PADDING, flipPadding) },
    },
    {
      name: 'preventOverflow',
      options: {
        altAxis: true,
        padding: merge({}, DEFAULT_PADDING, preventOverflowPadding),
      },
    },
    {
      name: 'arrow',
      enabled: arrow,
      options: {
        element: arrowRef,
      },
    },
  ], [offsetX, offsetY, flipPadding, preventOverflowPadding, arrow, arrowRef]);

  const handleOnExited = useCallback<
    NonNullable<MUIFadeProps['onExited']>
  >((element) => {
    if (onFadeExited) {
      onFadeExited(element);
    }
  }, [onFadeExited]);

  return (
    <MUIPopper
      sx={mergeSX(buildPopperSX({ arrowBackground }), sx)}
      {...restProps}
      modifiers={popperModifiers}
      transition
    >
      {({ TransitionProps }) => (
        <MUIFade
          {...TransitionProps}
          timeout={fadeTimeout}
          onExited={handleOnExited}
          unmountOnExit
        >
          <Box sx={mergeSX(buildContainerSX(), containerSX)}>
            {children}
            {arrow && <PopperArrow ref={setArrowRef} />}
          </Box>
        </MUIFade>
      )}
    </MUIPopper>
  );
};

export default Popper;
